import { Dispatch, SetStateAction, useLayoutEffect } from 'react';
import isEqual from 'react-fast-compare';

import { useStorage } from '@gmm/ui';

import { useCurrentTeacher } from './useCurrentTeacher';

export const useTeacherStorage = <S>(
  key: string,
  initialValue: S | (() => S),
  storage?: Storage,
): [S, Dispatch<SetStateAction<S>>] => {
  const { data: teacher } = useCurrentTeacher();
  const resolvedStorage = storage ?? window.localStorage;
  const storageKey = teacher ? `${teacher.id}.${key}` : key;
  const [storedValue, setStoredValue] = useStorage<S>(
    storageKey,
    initialValue,
    resolvedStorage,
  );

  useLayoutEffect(() => {
    if (!teacher) return;

    const oldStoredValue = resolvedStorage.getItem(key);
    const scopedStoredValue = resolvedStorage.getItem(storageKey);

    if (oldStoredValue === null) return;

    try {
      if (scopedStoredValue !== null) {
        resolvedStorage.removeItem(key);

        return;
      }

      resolvedStorage.setItem(storageKey, oldStoredValue);
      resolvedStorage.removeItem(key);

      const animationFrame = window.requestAnimationFrame(() => {
        try {
          window.dispatchEvent(
            new StorageEvent('storage', {
              key: storageKey,
              newValue: oldStoredValue,
              oldValue: null,
            }),
          );
        } catch {
          return;
        }
      });

      return () => window.cancelAnimationFrame(animationFrame);
    } catch {
      return;
    }
  }, [key, resolvedStorage, storageKey, teacher]);

  useLayoutEffect(() => {
    if (!teacher) return;

    const scopedStoredValue = resolvedStorage.getItem(storageKey);

    if (scopedStoredValue === null) return;

    try {
      const parsedScopedStoredValue = JSON.parse(scopedStoredValue) as S;

      if (isEqual(parsedScopedStoredValue, storedValue)) return;

      setStoredValue(parsedScopedStoredValue);
    } catch {
      return;
    }
  }, [resolvedStorage, setStoredValue, storageKey, storedValue, teacher]);

  return [storedValue, setStoredValue];
};
